import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Crown, Shield, Wand2, Check, X, ArrowLeft, Loader2, Copy, Gift, Mail, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tierConfig = {
  commoner: {
    icon: Shield,
    color: "from-stone-600 to-stone-800",
    border: "border-stone-600/40",
    badge: "bg-stone-700",
    accent: "text-stone-400",
  },
  adventurer: {
    icon: Crown,
    color: "from-amber-700 to-amber-900",
    border: "border-amber-500/40",
    badge: "bg-amber-700",
    accent: "text-amber-400",
  },
  archmage: {
    icon: Wand2,
    color: "from-violet-700 to-violet-900",
    border: "border-violet-500/40",
    badge: "bg-violet-700",
    accent: "text-violet-400",
  },
};

const features = [
  { label: "Campaigns", commoner: "1", adventurer: "5", archmage: "Unlimited" },
  { label: "Storage", commoner: "50 MB", adventurer: "500 MB", archmage: "2 GB" },
  { label: "AI Oracle (names, quests, family connections)", commoner: true, adventurer: true, archmage: true },
  { label: "SRD Bestiary (330+ creatures)", commoner: true, adventurer: true, archmage: true },
  { label: "SRD Items library", commoner: true, adventurer: true, archmage: true },
  { label: "DM Screen & Quick Reference", commoner: true, adventurer: true, archmage: true },
  { label: "42 Forgotten Realms Deities", commoner: true, adventurer: true, archmage: true, isNew: true },
  { label: "Full campaign management (NPCs, Locations, Buildings, Items, Factions, Quests, Lore)", commoner: true, adventurer: true, archmage: true },
  { label: "Encounter Builder & Combat Tracker", commoner: true, adventurer: true, archmage: true },
  { label: "Player Wiki (physical-tome layout, per-entity reveal)", commoner: true, adventurer: true, archmage: true, isNew: true },
  { label: "Multi-tab entity workspace", commoner: true, adventurer: true, archmage: true, isNew: true },
  { label: "Session Notes & AI session recap from audio", commoner: true, adventurer: true, archmage: true },
  { label: "Family Trees", commoner: true, adventurer: true, archmage: true },
  { label: "Static Maps + pins + battle notes", commoner: true, adventurer: true, archmage: true },
  // ---- Adventurer paywall ----
  { label: "Full live Virtual Tabletop (tokens · grid · drag-move · spell templates)", commoner: false, adventurer: true, archmage: true, isNew: true },
  { label: "Basic Fog of War (DM-painted reveals)", commoner: false, adventurer: true, archmage: true },
  { label: "Real-time multiplayer VTT", commoner: false, adventurer: true, archmage: true },
  { label: "AI Image Generation (portraits · creatures · items · handouts)", commoner: false, adventurer: true, archmage: true },
  { label: "Party Sharing & Player Wiki bulk-reveal", commoner: false, adventurer: true, archmage: true },
  { label: "Campaign Export (PDF / DOCX)", commoner: false, adventurer: true, archmage: true },
  // ---- Archmage paywall ----
  { label: "Dynamic Lighting Engine (walls + doors + windows + LOS raycast)", commoner: false, adventurer: false, archmage: true, isNew: true },
  { label: "Per-token Vision (Bright · Dim · Darkvision presets)", commoner: false, adventurer: false, archmage: true, isNew: true },
  { label: "Token-Wall Collision (tokens cannot phase through walls)", commoner: false, adventurer: false, archmage: true, isNew: true },
  { label: "Vision Mode toggle (Token Vision ↔ Fog of War, Roll20-style)", commoner: false, adventurer: false, archmage: true, isNew: true },
  { label: "Unlimited AI session recaps", commoner: false, adventurer: false, archmage: true },
  { label: "Priority email support & beta features", commoner: false, adventurer: false, archmage: true },
];

const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [myReferral, setMyReferral] = useState(null);
  const [contactEmail, setContactEmail] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [subRes, refRes, contactRes] = await Promise.all([
        axios.get(`${API}/subscription/me`, { withCredentials: true }),
        axios.get(`${API}/referral/code`, { withCredentials: true }),
        axios.get(`${API}/app/contact`).catch(() => ({ data: { email: "" } })),
      ]);
      setSubscription(subRes.data);
      setMyReferral(refRes.data);
      setContactEmail(contactRes.data.email);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const validateReferral = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(null);
      setReferralInfo(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/referral/validate/${code}`);
      setReferralValid(true);
      setReferralInfo(res.data);
    } catch {
      setReferralValid(false);
      setReferralInfo(null);
    }
  };

  // Stripe Payment Links (LIVE mode)
  const PAYMENT_LINKS = {
    adventurer: "https://buy.stripe.com/00w00bdAHdEzgmt65ecs800",
    archmage: "https://buy.stripe.com/4gMdR10NVasneelgJScs802",
  };

  const handleUpgrade = async (tier) => {
    if (tier === "commoner") return;
    setCheckoutLoading(tier);

    const paymentLink = PAYMENT_LINKS[tier];
    if (!paymentLink) {
      toast.error("Invalid tier selected");
      setCheckoutLoading(null);
      return;
    }

    // Append client_reference_id so Stripe webhook can identify the user
    const separator = paymentLink.includes("?") ? "&" : "?";
    const checkoutUrl = `${paymentLink}${separator}client_reference_id=${user?.user_id || ""}&prefilled_email=${encodeURIComponent(user?.email || "")}`;

    // Also record the intent in our backend for tracking
    try {
      await axios.post(`${API}/subscription/record-intent`, {
        tier,
        user_id: user?.user_id,
        referral_code: referralValid && referralCode ? referralCode : null,
      }, { withCredentials: true });
    } catch {
      // Non-blocking — proceed to checkout even if intent tracking fails
    }

    window.location.href = checkoutUrl;
  };

  const copyReferralLink = () => {
    if (!myReferral?.code) return;
    const link = `${window.location.origin}/subscription?ref=${myReferral.code}`;
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied!");
  };

  const currentTier = subscription?.tier || "commoner";

  // Check URL for referral code on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setReferralCode(ref);
      validateReferral(ref);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-muted-foreground" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Signed in as</span>
            <span className="text-sm font-medium">{user?.name}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-12 text-center">
        <h1 className="text-4xl sm:text-5xl font-serif font-bold tracking-tight mb-4">
          Choose Your <span className="text-primary">Destiny</span>
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Every great adventure begins with a choice. Select the path that matches your ambition.
        </p>

        {currentTier !== "commoner" && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30">
            <Crown className="w-4 h-4 text-primary" />
            <span className="text-sm">Current plan: <strong className="capitalize">{currentTier}</strong></span>
          </div>
        )}
      </div>

      {/* Referral Code Input (for Commoner users) */}
      {currentTier === "commoner" && (
        <div className="max-w-6xl mx-auto px-6 pb-6">
          <div className="max-w-md mx-auto bg-card border border-border/50 rounded-lg p-4" data-testid="referral-input-section">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium">Have a referral code?</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter referral code (e.g. REF-ABC123)"
                value={referralCode}
                onChange={(e) => {
                  setReferralCode(e.target.value);
                  validateReferral(e.target.value);
                }}
                className="flex-1"
                data-testid="referral-code-input"
              />
            </div>
            {referralValid === true && (
              <p className="text-xs text-green-400 mt-2" data-testid="referral-valid">
                Referred by {referralInfo?.referrer_name} — Adventurer tier at £15/yr instead of £20!
              </p>
            )}
            {referralValid === false && (
              <p className="text-xs text-red-400 mt-2" data-testid="referral-invalid">
                Invalid referral code
              </p>
            )}
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="max-w-6xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {Object.entries(tierConfig).map(([key, config]) => {
            const Icon = config.icon;
            const isCurrent = currentTier === key;
            const tier = {
              commoner: { name: "Commoner", price: "Free", desc: "Build your world — forever free" },
              adventurer: { name: "Adventurer", price: "20", desc: "For DMs running live tabletop sessions" },
              archmage: { name: "Archmage", price: "30", desc: "The full Roll20-tier experience" },
            }[key];

            const hasReferralDiscount = referralValid && key === "adventurer" && currentTier === "commoner";

            return (
              <div
                key={key}
                className={`relative rounded-lg border ${config.border} bg-card overflow-hidden transition-all duration-300 hover:scale-[1.02] ${
                  key === "adventurer" ? "md:-mt-4 md:mb-4 ring-1 ring-amber-500/30" : ""
                }`}
                data-testid={`tier-card-${key}`}
              >
                {key === "adventurer" && (
                  <div className="bg-amber-600 text-center py-1 text-xs font-bold tracking-wider uppercase text-white">
                    Most Popular
                  </div>
                )}
                
                <div className={`p-1 bg-gradient-to-b ${config.color}`}>
                  <div className="bg-card p-6 rounded-t-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-lg ${config.badge} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-serif font-bold">{tier.name}</h3>
                        <p className="text-xs text-muted-foreground">{tier.desc}</p>
                      </div>
                    </div>

                    <div className="mb-6">
                      {key === "commoner" ? (
                        <div className="text-3xl font-bold">{tier.price}</div>
                      ) : (
                        <div>
                          {hasReferralDiscount ? (
                            <div className="flex items-baseline gap-2">
                              <span className="text-sm text-muted-foreground">£</span>
                              <span className="text-3xl font-bold text-amber-400">15</span>
                              <span className="text-lg text-muted-foreground line-through">£{tier.price}</span>
                              <span className="text-sm text-muted-foreground">/ year</span>
                            </div>
                          ) : (
                            <div className="flex items-baseline gap-1">
                              <span className="text-sm text-muted-foreground">£</span>
                              <span className="text-3xl font-bold">{tier.price}</span>
                              <span className="text-sm text-muted-foreground">/ year</span>
                            </div>
                          )}
                          {hasReferralDiscount && (
                            <p className="text-xs text-amber-400 mt-1">Referral discount applied!</p>
                          )}
                          {!hasReferralDiscount && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {key === "adventurer" ? "Less than £1.70/month" : "Less than £2.50/month"}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <ul className="space-y-2.5 mb-6">
                      {features.map((feat) => {
                        const val = feat[key];
                        const enabled = val === true || typeof val === "string";
                        return (
                          <li key={feat.label} className="flex items-start gap-2.5 text-sm">
                            {enabled ? (
                              <Check className={`w-4 h-4 mt-0.5 shrink-0 ${config.accent}`} />
                            ) : (
                              <X className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground/40" />
                            )}
                            <span className={enabled ? "text-foreground" : "text-muted-foreground/50"}>
                              {feat.label}
                              {typeof val === "string" && <span className={`ml-1 font-medium ${config.accent}`}>({val})</span>}
                              {feat.isNew && enabled && (
                                <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-400 border border-amber-500/30 align-middle">NEW</span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>

                    {isCurrent ? (
                      <Button disabled className="w-full" data-testid={`tier-btn-${key}`}>
                        Current Plan
                      </Button>
                    ) : key === "commoner" ? (
                      <Button variant="outline" disabled className="w-full" data-testid={`tier-btn-${key}`}>
                        Free Forever
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleUpgrade(key)}
                        disabled={checkoutLoading === key}
                        className={`w-full ${key === "adventurer" ? "bg-amber-600 hover:bg-amber-700" : "bg-violet-600 hover:bg-violet-700"} text-white`}
                        data-testid={`tier-btn-${key}`}
                      >
                        {checkoutLoading === key ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : null}
                        {hasReferralDiscount ? "Upgrade for £15" : currentTier !== "commoner" && key !== "commoner" ? "Switch Plan" : "Upgrade Now"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Referral Sharing Section (for paid users) */}
      {currentTier !== "commoner" && myReferral?.code && (
        <div className="max-w-6xl mx-auto px-6 pb-8">
          <div className="bg-card border border-border/50 rounded-lg p-6" data-testid="referral-share-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-700 flex items-center justify-center">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-serif font-bold">Refer a Fellow Adventurer</h3>
                <p className="text-sm text-muted-foreground">They get Adventurer at £15/yr, you get 1 month free per referral</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="flex-1 bg-muted/20 border border-border/50 rounded-sm px-4 py-3 font-mono text-sm select-all" data-testid="referral-code-display">
                {myReferral.code}
              </div>
              <Button onClick={copyReferralLink} className="shrink-0" data-testid="copy-referral-link">
                <Copy className="w-4 h-4 mr-2" /> Copy Referral Link
              </Button>
            </div>

            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>{myReferral.referral_count} successful referral{myReferral.referral_count !== 1 ? "s" : ""}</span>
              </div>
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4" />
                <span>{myReferral.bonus_days_earned} bonus days earned</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Summary */}
      {subscription && (
        <div className="max-w-6xl mx-auto px-6 pb-8">
          <div className="bg-card border border-border/50 rounded-lg p-6" data-testid="usage-summary">
            <h3 className="text-lg font-serif font-bold mb-4">Your Usage</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Campaigns</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{subscription.campaigns_owned}</span>
                  <span className="text-sm text-muted-foreground">/ {subscription.campaigns_limit >= 999999 ? "Unlimited" : subscription.campaigns_limit}</span>
                </div>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.min(100, (subscription.campaigns_owned / Math.min(subscription.campaigns_limit, 100)) * 100)}%` }} />
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Storage</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{(subscription.storage_used_bytes / (1024*1024)).toFixed(1)}</span>
                  <span className="text-sm text-muted-foreground">/ {(subscription.storage_limit_bytes / (1024*1024)).toFixed(0)} MB</span>
                </div>
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.min(100, (subscription.storage_used_bytes / subscription.storage_limit_bytes) * 100)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer with Contact */}
      {contactEmail && (
        <div className="border-t border-border/30 mt-8">
          <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>NaturalOne-Chronicles</span>
            <a href={`mailto:${contactEmail}`} className="flex items-center gap-2 hover:text-foreground transition-colors" data-testid="contact-email">
              <Mail className="w-4 h-4" />
              {contactEmail}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
